import { defineStore } from 'pinia';
import $fetch from '~/modules/fetch';
import { requirejs } from '~/modules/require';

import { useReferentialStore } from './referential';
import { useFileStore } from './file';
import { useMeStore } from './me';
import { useLabExplorationStore } from './lab-exploration';

// Lazy-loaded AMD modules cache
let _cartService: any = null;
let _conversionHelpers: any = null;
let _settings: any = null;
let _orderHelpers: any = null;
let _tracker: any = null;

async function getModules() {
  if (!_cartService) {
    [_cartService, _conversionHelpers, _settings, _orderHelpers, _tracker] = await requirejs<any[]>(
      [
        'DS/MP3DPApplicationStore/MP3DPCartService',
        'DS/MP3DPApplicationStore/MP3DPConversionHelpers',
        'DS/MP3DPSettings/MP3DPSettings',
        'DS/MP3DPApplicationStore/MP3DPOrderHelpers',
        'DS/MP3DPSettings/MP3DPTracker',
      ],
    );
  }

  return {
    MP3DPCartService: _cartService,
    MP3DPConversionHelpers: _conversionHelpers,
    MP3DPSettings: _settings,
    MP3DPOrderHelpers: _orderHelpers,
    MP3DPTracker: _tracker,
  };
}

let pullingTimeoutIds: Array<ReturnType<typeof setTimeout>> = [];

export const useCartStore = defineStore({
  id: 'cart',
  state: () => ({
    cart: {
      Items: [
        {
          currentState: 'DRAFT',
          DeliveryServiceName: 'Shipping',
          IsTestItem: false,
          Quantity: 1,
          '@type': 'MP_DeliveryServiceRequest',
        },
      ] as any[],
      Currency: '',
      currentState: 'DRAFT',
      IsDirectPurchase: 'FALSE',
      IsPriceDetailsDisplay: true,
      BillTo: '',
      ShopID: '',
      '@type': 'MP_Cart',
    } as any,
    addinStatuses: {} as Record<string, any>,
    addinWaitingUpdates: {} as Record<string, any>,
    empMode: false as boolean,
    currentPrintItem: {} as any,
    services: [] as any[],
    annotations: [] as any[],
    finishes: [] as any[],
    contract: {} as any,
    documents: {} as Record<string, any>,
    labsCount: 0 as number,
    selectedLabs: [] as any[],
    labs: {} as Record<string, any>,
    shipping: {} as Record<string, any>,
    estimationShippingCountry:
      typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('estimationShippingCountry') || ''
        : '',
    shippingAddressId: '' as string,
    price: {} as Record<string, any>,
    taxes: [] as any[],
    CMSMaterialSelection: {} as Record<string, any[]>,
    isShippingLoading: false as boolean,
    isLabsLoading: false as boolean,
    isPricingLoading: false as boolean,
    isTaxesLoading: false as boolean,
    waitingTaxes: true as boolean,
    ressources: {} as Record<string, any>,
    exploreMode:
      typeof sessionStorage !== 'undefined' && sessionStorage.getItem('exploreMode') === 'true',
    purchaseMode:
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('purchaseMode') || '' : '',
    testPartMode:
      typeof sessionStorage !== 'undefined' && sessionStorage.getItem('testPartMode') === 'true',
    cartMustBeCreatedInBack: false as boolean,
    'saved-cart':
      typeof localStorage !== 'undefined' && localStorage.getItem('saved-cart')
        ? JSON.parse(localStorage.getItem('saved-cart')!)
        : {},
  }),

  getters: {
    cartItems(s): any[] {
      return s.cart.Items || [];
    },
    printItems(): any[] {
      return this.cartItems.filter(
        (el: any) => el['@type'] === 'MP3DP_PrintRequest' || el.type === 'MP3DP_PrintRequest',
      );
    },
    deliveryItem(): any {
      return this.cartItems.find(
        (el: any) =>
          el['@type'] === 'MP_DeliveryServiceRequest' || el.type === 'MP_DeliveryServiceRequest',
      );
    },
    finishItems(): any[] {
      return this.cartItems.filter(
        (el: any) => el['@type'] === 'MP3DP_FinishRequest' || el.type === 'MP3DP_FinishRequest',
      );
    },
    servicesItems(): any[] {
      return this.cartItems.filter(
        (el: any) =>
          el['@type'] === 'MP_AdditionalServiceRequest' ||
          el.type === 'MP_AdditionalServiceRequest',
      );
    },
    arePrintItemsUploadedToFcs(): boolean {
      return this.printItems.every((el: any) => el.backendId && el.Part3DPrint);
    },
    finishItemsByCartItem(s): Record<string, any[]> {
      return this.finishItems.reduce((acc: Record<string, any[]>, finish: any) => {
        let localRelatedCartItemID = finish.RelatedCartItemID;

        if (!finish.RelatedCartItemID.startsWith('part-')) {
          const relatedCartItem = (s.cart.Items || []).find(
            (item: any) =>
              item.backendId === finish.RelatedCartItemID || item.id === finish.RelatedCartItemID,
          );
          localRelatedCartItemID = relatedCartItem
            ? `part-${relatedCartItem.Part3DPrintName}`
            : null;
        }

        if (localRelatedCartItemID) {
          if (!acc[localRelatedCartItemID]) {
            acc[localRelatedCartItemID] = [];
          }

          const existingFinishIndex = acc[localRelatedCartItemID].findIndex(
            (f: any) => f.FinishName === finish.FinishName,
          );

          if (existingFinishIndex !== -1) {
            acc[localRelatedCartItemID][existingFinishIndex] = finish;
          } else {
            acc[localRelatedCartItemID].push(finish);
          }
        }

        return acc;
      }, {});
    },

    // FIX: renamed from currentPrintItem to currentPrintItemGetter to avoid collision with state property.
    // IMPORTANT: update all consumers that previously used store.currentPrintItem expecting the resolved object.
    currentPrintItemGetter(s): any {
      let item: any;

      if (s.currentPrintItem) {
        item = this.printItems.find((el: any) => el.id === s.currentPrintItem);
      }

      if (!item) {
        [item] = this.printItems;
      }

      return item || {};
    },
    printItem(): (id: string) => any {
      return (id: string) => this.printItems.find((printItem: any) => printItem.id === id);
    },
    printItemByName(): (name: string) => any {
      return (name: string) =>
        this.printItems.find((printItem: any) => printItem.Part3DPrintName === name);
    },

    // FIX: restored — was present in Vue 2 but missing in Vue 3 conversion
    printItemDimensionInCm(): (printItemId: string) => any {
      return (printItemId: string) => {
        const printItem = this.printItems.find((el: any) => el.id === printItemId) || {};

        return {
          volume: printItem.Volume || 0,
          surface: printItem.Surface || 0,
          size: {
            x: printItem.BoundingBoxX || 0,
            y: printItem.BoundingBoxY || 0,
            z: printItem.BoundingBoxZ || 0,
          },
        };
      };
    },

    // FIX: restored — was completely missing from the Vue 3 conversion
    printItemDimensionInUnit(): (printItemId: string) => any {
      return (printItemId: string) => {
        const printItem = this.printItems.find((el: any) => el.id === printItemId) || {};
        const unit = printItem.Unit;
        const dimension = this.printItemDimensionInCm(printItemId);

        if (!_conversionHelpers) {
          return { volume: 0, surface: 0, size: { x: 0, y: 0, z: 0 } };
        }

        const size = _conversionHelpers.getSizeFromCm(dimension.size, unit);

        return {
          volume: _conversionHelpers.getVolumeFromCm3(dimension.volume, unit) || 0,
          surface: _conversionHelpers.getSurfaceFromCm2(dimension.surface, unit) || 0,
          size: size || { x: 0, y: 0, z: 0 },
        };
      };
    },

    // FIX: restored — was missing from the Vue 3 conversion
    currentPrintItemDimensionInCm(): any {
      return this.printItemDimensionInCm(this.currentPrintItemGetter.id);
    },

    // FIX: restored — was missing from the Vue 3 conversion
    currentPrintItemDimensionInUnit(): any {
      return this.printItemDimensionInUnit(this.currentPrintItemGetter.id);
    },

    dimensionsHasBeenCalculatedByPrintItemId(): Record<string, boolean> {
      return this.printItems.reduce((acc: Record<string, boolean>, el: any) => {
        acc[el.id] = Boolean(
          parseFloat(el.BoundingBoxX) || parseFloat(el.BoundingBoxY) || parseFloat(el.BoundingBoxZ),
        );

        return acc;
      }, {});
    },
    areDimensionsReady(): boolean {
      return (
        this.printItems.length > 0 &&
        Object.values(this.dimensionsHasBeenCalculatedByPrintItemId).every((el: any) => el)
      );
    },

    // FIX: restored — was missing from the Vue 3 conversion
    is2DFormat(): boolean {
      if (!_settings) {
        return false;
      }

      return _settings
        .get('general', 'twoDimensionsExtensions')
        .includes(this.currentPrintItemGetter.Format);
    },

    // FIX: restored — was missing from the Vue 3 conversion
    canAskForUnit(): boolean {
      if (!_settings) {
        return false;
      }

      return _settings
        .get('general', 'askUnitForExtensions')
        .includes(this.currentPrintItemGetter.Format);
    },

    shippingPrice(s): (id: string) => any {
      return (id: string) =>
        this.deliveryItem?.DeliveryServiceName === 'Shipping' ? s.shipping[id] : '-';
    },
    taxesPrice(s): number {
      return (s.taxes as any).taxesAmountTotal || 0;
    },
    labPricing(s): (id: string) => any {
      return (id: string) => (this.pricingParams[id] && s.price[id]) || {};
    },
    totalPrice(): (id: string) => any {
      return (id: string) =>
        this.labPricing(id).totalPrice > 0
          ? this.labPricing(id).totalPrice - (this.labPricing(id).quotePrice || 0)
          : null;
    },
    grandTotalPrice(): (id: string) => any {
      return (id: string) => {
        const hasOffers = this.hasOffersForPrintItems(id);
        const shippingPrice = !this.shippingPrice(id) ? '' : this.shippingPrice(id).toString();
        const shippingPriceIsRange =
          shippingPrice &&
          (shippingPrice.includes('+') ||
            (shippingPrice.includes('-') && shippingPrice.charAt(0) !== '-'));

        if (shippingPriceIsRange) {
          return null;
        }

        return hasOffers
          ? parseFloat(this.totalPrice(id)) +
              parseFloat(this.shippingPrice(id)) +
              (this.labPricing(id).quotePrice || 0) || null
          : null;
      };
    },
    grandTotalPriceIncludesTaxes(): (id: string) => any {
      return (id: string) =>
        parseFloat(this.grandTotalPrice(id)) + parseFloat(String(this.taxesPrice)) || null;
    },
    cartItemPrice(): (labId: string, itemId: string) => any {
      return (labId: string, itemId: string) =>
        ((this.labPricing(labId).parts || {})[itemId] || {}).totalPrice || '-';
    },

    // FIX: restored — was completely missing from the Vue 3 conversion
    finishItemPrice(): (labId: string, finish: string) => any {
      return (labId: string, finish: string) => {
        const offer =
          this.labFinishOffers(labId).find((o: any) => o.product.optionName === finish) || {};

        return ((this.labPricing(labId).parts || {})[offer.id] || {}).totalPrice || '-';
      };
    },

    // FIX: restored — was completely missing from the Vue 3 conversion
    serviceItemPrice(): (labId: string, service: string) => any {
      return (labId: string, service: string) => {
        const offer =
          this.labServiceOffers(labId).find(
            (o: any) => o.product.additionalServiceName === service,
          ) || {};

        return ((this.labPricing(labId).parts || {})[offer.id] || {}).totalPrice || '-';
      };
    },

    labSelected(s): (i: number | string) => any {
      return (i: number | string) => {
        if (typeof i === 'number') {
          return s.selectedLabs[i] || {};
        }

        return s.selectedLabs.find((el: any) => el.id === i) || {};
      };
    },
    lab(s): (i: number | string) => any {
      return (i: number | string) => {
        const id = typeof i === 'number' ? this.labSelected(i).id : i;

        return s.labs[id] || { shopServices: { MP3DP_PrintShopService: { products: [] } } };
      };
    },
    labService(): (id: string) => any {
      return (id: string) => this.lab(id).shopServices.MP3DP_PrintShopService;
    },
    labProducts(): (id: string) => any[] {
      return (id: string) => this.labService(id).products;
    },
    labOffers(): (id: string) => any[] {
      return (id: string) =>
        this.labProducts(id).flatMap((product: any) =>
          (product.offers || []).map((offer: any) => ({ product, ...offer })),
        );
    },
    labServiceOffers(): (id: string) => any[] {
      return (id: string) =>
        this.labOffers(id).filter((offer: any) =>
          ['MP3DP_AdditionalService', 'MP3DP_CustomAdditionalService'].includes(offer.product.type),
        );
    },
    labFinishOffers(): (id: string) => any[] {
      return (id: string) => {
        let finish = this.labOffers(id).filter((offer: any) =>
          ['MP3DP_Finish', 'MP3DP_CustomFinish'].includes(offer.product.type),
        );
        const labOffers = this.labSelectedOffers(id);

        if (Object.keys(labOffers).length) {
          const labOffersId = Object.values(labOffers).map((el: any) => el.id);
          finish = finish.filter((offer: any) => labOffersId.includes(offer.relatedOffer));
        } else {
          finish = finish
            .filter((offer: any) =>
              this.labOffers(id).find(
                (o: any) =>
                  o.id === offer.relatedOffer &&
                  (this.printItems[0].Process
                    ? o.product.processId === this.printItems[0].Process
                    : true) &&
                  o.product.processFamilyId === this.printItems[0].ProcessFamily,
              ),
            )
            .filter(
              (offer: any) =>
                offer.finishOfferMaterialFamilyId === this.printItems[0].MaterialCategory &&
                (this.printItems[0].MaterialSubCategory
                  ? offer.finishOfferMaterialClassId === this.printItems[0].MaterialSubCategory
                  : true) &&
                (this.printItems[0].MaterialType
                  ? offer.finishOfferMaterialTypeId === this.printItems[0].MaterialType
                  : true) &&
                (this.printItems[0].MaterialID
                  ? offer.finishOfferMaterialId === this.printItems[0].MaterialID
                  : true),
            );
        }

        return finish;
      };
    },
    labShippingOffer(s): Record<string, string> {
      return s.selectedLabs.reduce((acc: Record<string, string>, lab: any) => {
        const deliveryProduct = this.labProducts(lab.id).find(
          (product: any) =>
            product.type === 'MP_DeliveryService' &&
            product.deliveryServiceName === 'Shipping' &&
            product.offers &&
            product.offers[0].currentState === 'ENABLED',
        );

        if (deliveryProduct) {
          acc[lab.id] = deliveryProduct.offers[0].id;
        }

        return acc;
      }, {});
    },
    labPickupOffer(s): Record<string, string> {
      return s.selectedLabs.reduce((acc: Record<string, string>, lab: any) => {
        const deliveryProduct = this.labProducts(lab.id).find(
          (product: any) =>
            product.type === 'MP_DeliveryService' &&
            product.deliveryServiceName === 'Pickup' &&
            product.offers &&
            product.offers[0].currentState === 'ENABLED',
        );

        if (deliveryProduct) {
          acc[lab.id] = deliveryProduct.offers[0].id;
        }

        return acc;
      }, {});
    },
    hasOffersForPrintItems(): (id: string) => boolean {
      return (id: string) => {
        const labOffers = this.labSelectedOffers(id);

        return this.printItems.every((item: any) => labOffers[item.id]);
      };
    },
    partItemsId(): string[] {
      return this.printItems.map((el: any) => el.Part3DPrint).filter((el: any) => Boolean(el));
    },

    // FIX: restored missing `processFamily` field that was dropped in the Vue 3 conversion.
    // Note: the referential store getter `mapProcessFamiliesByFormat` must be accessed via its own store.
    shopsV10Params(_s): any {
      const params: any = { items: [] };
      params.items = this.printItems.map((item: any) => {
        let processFamilyValue: any;

        try {
          const referential = useReferentialStore();
          processFamilyValue = item.ProcessFamily
            ? [item.ProcessFamily.toString()]
            : referential.mapProcessFamiliesByFormat[item.Format?.toUpperCase()];
        } catch {
          processFamilyValue = item.ProcessFamily ? [item.ProcessFamily.toString()] : undefined;
        }

        const param: any = {
          itemId: item.id,
          process: item.Process ? item.Process.toString() : '',
          processFamily: processFamilyValue,
          material: item.MaterialID ? item.MaterialID.toString() : '',
          materialType: item.MaterialType ? item.MaterialType.toString() : '',
          materialSpecificType: item.MaterialSubCategory ? item.MaterialSubCategory.toString() : '',
          materialGenericType: item.MaterialCategory ? item.MaterialCategory.toString() : '',
          toleranceMin: item.ToleranceMin ? item.ToleranceMin.toString() : '',
          bboxx: item.BoundingBoxX ? item.BoundingBoxX.toString() : '0',
          bboxy: item.BoundingBoxY ? item.BoundingBoxY.toString() : '0',
          bboxz: item.BoundingBoxZ ? item.BoundingBoxZ.toString() : '0',
          quantity: item.Quantity ? item.Quantity.toString() : '0',
          volume: item.Volume ? item.Volume.toString() : '0',
          thicknessUnit: item.thicknessUnit || 'mm',
          thicknessValues: item.thicknessValues ? item.thicknessValues : [],
        };

        Object.keys(param).forEach(
          (key) => (param[key] === null || param[key] === '') && delete param[key],
        );

        // No bounding box for Cutting and Forming process families
        if (
          param.processFamily &&
          ['PF-1e81bc3b8e406dd1a552f45c89bf7ac9', 'PF-1e81bc3c88696e11870df45c89bf7ac9'].includes(
            param.processFamily,
          )
        ) {
          delete param.bboxx;
          delete param.bboxy;
          delete param.bboxz;
        }

        return param;
      });

      params.deliveryCountryIso3 = this.shippingAddressCode || 'USA';

      return params;
    },
    // SHIPPING
    shippingAddress(s) {
      const me = useMeStore();
      return me.address(s.shippingAddressId);
    },
    shippingAddressCode(s): string {
      const me = useMeStore();

      if (s['saved-cart']?.ShipTo) {
        const country = me.countryByName(s['saved-cart'].ShipTo.country);

        return country.isocode3;
      }

      const addr = me.address(s.shippingAddressId);

      if (addr.country) {
        return (addr.country || '').split('/').pop();
      }

      if (s.estimationShippingCountry) {
        return s.estimationShippingCountry;
      }

      if (me.lastCreatedShippingAddress) {
        return me.lastCreatedShippingAddress.country.split('/').pop();
      }

      if (me.lastCreatedBillingAddress) {
        return me.lastCreatedBillingAddress.country.split('/').pop();
      }

      return me.estimatedUserLocation || me.userCountry?.isocode3 || 'USA';
    },

    labSelectedOffers(): (i: number | string) => Record<string, any> {
      return (i: number | string) => {
        const selectedLabId = this.labSelected(i).id;

        if (!selectedLabId) {
          return {};
        }

        try {
          const labExploration = useLabExplorationStore();
          const lab = labExploration.getLab(selectedLabId);

          if (!lab) {
            return {};
          }

          return Object.fromEntries(lab.offers.map((el: any) => [el.itemId, el]));
        } catch {
          return {};
        }
      };
    },

    pricingParams(s): Record<string, any> {
      const labs = this.selectedLabs.filter(
        (el: any) => Object.values(this.labSelectedOffers(el.id)).length,
      );

      if (!labs.length) {
        return {};
      }

      /* If (!s.selectedLabs.every((el: any) => this.lab(el.id).id)) {
                return {};
            } */

      if (!this.cartItems.every((el: any) => el.id)) {
        return {};
      }

      return labs.reduce((acc: Record<string, any>, lab: any) => {
        const finishOffers = this.labFinishOffers(lab.id);
        const serviceOffers = this.labServiceOffers(lab.id);
        const labOffers = this.labSelectedOffers(lab.id);

        acc[lab.id] = this.printItems.map((item: any) => ({
          bboxx: parseFloat(item.BoundingBoxX),
          bboxy: parseFloat(item.BoundingBoxY),
          bboxz: parseFloat(item.BoundingBoxZ),
          volume: parseFloat(item.Volume),
          surface: parseFloat(item.Surface),
          offers: [
            {
              cartItemId: item.id,
              id: labOffers[item.id]?.id,
              quantity: parseInt(item.Quantity, 10),
            },
          ].concat(
            s.services
              .map((service: any) => {
                const offer = serviceOffers.find(
                  (el: any) => el.product.additionalServiceName === service,
                );

                if (!offer) {
                  return null;
                }

                return { id: offer.id, quantity: parseInt(item.Quantity, 10) };
              })
              .concat(
                (this.finishItemsByCartItem[item.id] || []).map((finish: any) => {
                  const offer = finishOffers.find(
                    (el: any) => el.product.optionName === finish.FinishName,
                  );

                  if (!offer || offer.pricingOption !== 'ExactPrice') {
                    return null;
                  }

                  return {
                    id: offer.id,
                    quantity: parseInt(item.Quantity, 10),
                    cartItemId: finish.id,
                  };
                }),
              )
              .filter((el: any) => el),
          ),
        }));

        return acc;
      }, {});
    },

    cartItemsWeight(): Record<string, number> {
      if (this.printItems.some((el: any) => !el.Volume || !el.MaterialCategory)) {
        return {};
      }

      return this.printItems.reduce((acc: Record<string, number>, item: any) => {
        if (_cartService) {
          acc[item.id] =
            (item.Volume *
              _cartService.getMaterialDensity(item.MaterialCategory, item.MaterialSubCategory)) /
            1000;
        }

        return acc;
      }, {});
    },

    totalWeight(): number {
      return this.printItems.reduce(
        (acc: number, item: any) =>
          acc + (this.cartItemsWeight[item.id] ? this.cartItemsWeight[item.id] : 0) * item.Quantity,
        0,
      );
    },

    finishPricesByPrintItem(): Record<string, number> {
      return this.printItems.reduce((acc: Record<string, number>, printItem: any) => {
        acc[printItem.id] = this.finishItemsByCartItem[printItem.id]
          ? this.finishItemsByCartItem[printItem.id].reduce((total: number, finishItem: any) => {
              const finishPrice = this.labPricing(this.lab(0).id).parts?.[finishItem.id];
              total += finishPrice ? finishPrice.totalPrice : 0;

              return total;
            }, 0)
          : 0;

        return acc;
      }, {});
    },
  },

  actions: {
    // ─── STATE SETTERS (replaces Vuex mutations) ──────────────────────────────

    setLoading(type: string, value: boolean) {
      if (type in this.$state) {
        (this.$state as any)[type] = value;
      }
    },
    setEmpMode(payload: boolean) {
      this.empMode = payload;
    },
    setNdaDocument(payload: any) {
      this.contract = payload || {};
    },
    addDocument(payload: { name: string; id: string }) {
      this.documents[payload.name] = payload.id;
    },
    removeDocument(payload: { name: string }) {
      delete this.documents[payload.name];
    },
    removeDocuments() {
      this.documents = {};
    },
    setLabsCount(payload: number) {
      this.labsCount = payload || 0;
    },
    setSelectedLabs(payload: any[]) {
      if (payload && Array.isArray(payload)) {
        this.selectedLabs = payload.filter((lab: any) => lab.id);
        sessionStorage.setItem('selectedLabs', JSON.stringify(this.selectedLabs));
      }
    },
    removeLabFromSelectedLabs(payload: string) {
      if (!payload) {
        return;
      }

      this.selectedLabs.splice(
        this.selectedLabs.findIndex((el: any) => el.id === payload),
        1,
      );
    },
    setShippingAddress(payload: string) {
      this.shippingAddressId = payload;
    },
    setShippingPrice(payload: { id: string; price: any }) {
      this.shipping[payload.id] = payload.price;
    },
    setEstimationCountryShippingPrice(countryCode: string) {
      sessionStorage.setItem('estimationShippingCountry', countryCode || '');
      this.estimationShippingCountry = countryCode;
    },
    setPrice(payload: { id: string; price: any }) {
      this.price[payload.id] = payload.price;
    },
    setTaxes(payload: any) {
      this.taxes = payload || [];
    },
    setWaitingTaxes(payload: boolean) {
      this.waitingTaxes = payload;
    },
    setLab(lab: any) {
      this.labs[lab.id] = lab;
    },
    setServices(payload: any[]) {
      this.services = payload;
    },
    setFinishes(payload: any[]) {
      this.finishes = payload;
    },
    setCart(payload: any) {
      const updatedCart = {
        ...this.cart,
        ...payload,
        Items:
          payload.Items?.map((item: any) => ({
            ...item,
            CartID: item.CartID || payload.id || this.cart.id,
          })) || this.cart.Items,
      };

      updatedCart.Items.filter(
        (i: any) => i['@type'] === 'MP3DP_PrintRequest' || i.type === 'MP3DP_PrintRequest',
      ).forEach((item: any) => {
        if (!item.id.startsWith('part-')) {
          item.backendId = item.id || null;
          item.id = `part-${item.Part3DPrintName.replace('_V2', '')}`;
        }
      });

      updatedCart.Items.filter(
        (i: any) => i['@type'] === 'MP3DP_FinishRequest' || i.type === 'MP3DP_FinishRequest',
      ).forEach((finish: any) => {
        if (!finish.id.includes('finish-')) {
          const relatedCartItem =
            updatedCart.Items.find((i: any) => i.backendId === finish.RelatedCartItemID) ||
            this.cart.Items.find((i: any) => i.backendId === finish.RelatedCartItemID);
          finish.backendId = finish.id || null;
          finish.id = `${relatedCartItem.Part3DPrintName}-finish-${finish.FinishName}`;
          finish.RelatedCartItemID = relatedCartItem.id;
        }
      });

      this.cart = updatedCart;
    },
    setSavedCart(payload: any) {
      this['saved-cart'] = { ...this.cart, ...payload };
    },
    setCartItems(payload: any[]) {
      this.cart.Items = payload;
    },
    setAddinStatus({ id, status }: { id: string; status: string }) {
      this.addinStatuses[id] = status;

      if (status === 'uptodate') {
        this.addinWaitingUpdates[id] = {};
      }
    },
    setAddinUpdate({ id, data }: { id: string; data: any }) {
      this.addinWaitingUpdates[id] = data;
      this.addinStatuses[id] = 'outdated';
    },
    setExploreMode(exploreMode: boolean) {
      this.exploreMode = exploreMode;
      sessionStorage.setItem('exploreMode', String(exploreMode));
    },
    convertRelatedCartItemIdToBackendId(payload: any) {
      if (payload.RelatedCartItemID?.startsWith('part-')) {
        const relatedCartItem = this.cart.Items.find(
          (i: any) => i.id === payload.RelatedCartItemID,
        );
        payload.RelatedCartItemID = relatedCartItem.backendId || relatedCartItem.id;
      }
    },
    addCartItem(payload: any) {
      this.convertRelatedCartItemIdToBackendId(payload);
      payload.forceAdd = true;
      this.cart.Items.push(payload);
    },
    removeItem(payload: any) {
      if (
        typeof payload === 'string' &&
        this.cart.Items.findIndex((item: any) => item.id === payload) !== -1
      ) {
        this.cart.Items.splice(
          this.cart.Items.findIndex((item: any) => item.id === payload),
          1,
        );
      } else if (this.cart.Items.indexOf(payload) !== -1) {
        this.cart.Items.splice(this.cart.Items.indexOf(payload), 1);
      }
    },
    resetStore(payload?: any) {
      sessionStorage.removeItem('addin_backup');

      if (!payload || (payload && !payload.noResetLabs)) {
        sessionStorage.removeItem(`selectedLabs-${this.cart.id}`);
      }

      if (!payload || (payload && !payload.noResetExploreMode)) {
        sessionStorage.removeItem('exploreMode');
      }

      if (_cartService) {
        this.cart = _cartService.getDefaultCart();
      } else {
        this.cart = {
          Items: [
            {
              currentState: 'DRAFT',
              DeliveryServiceName: 'Shipping',
              IsTestItem: false,
              Quantity: 1,
              '@type': 'MP_DeliveryServiceRequest',
            },
          ],
          Currency: '',
          currentState: 'DRAFT',
          IsDirectPurchase: 'FALSE',
          IsPriceDetailsDisplay: true,
          BillTo: '',
          ShopID: '',
          '@type': 'MP_Cart',
        };
      }

      this.currentPrintItem = {};
      this.services = [];
      this.annotations = [];
      this.finishes = [];
      this.shippingAddressId = '';
      this.taxes = [];
      this.isShippingLoading = false;
      this.isPricingLoading = false;
      this.isTaxesLoading = false;
      this.contract = {};
      this.documents = {};
      this.shipping = {};
      this.price = {};
      this.CMSMaterialSelection = {};
      this['saved-cart'] = {};
    },
    setCurrentPrintItem(payload: any) {
      this.currentPrintItem = payload;
    },
    setPrintItemsValue(obj: any) {
      this.printItems.forEach((printItem: any) => {
        this.setCartItemValue({ id: printItem.id, data: obj });
      });
    },
    setCartItemsState(status: string) {
      this.cart.Items.forEach((el: any) => {
        el.currentState = status;
      });
    },
    async setCartItemValue(payload: { id?: string; name?: string; data: any }) {
      const { MP3DPConversionHelpers } = await getModules();

      const cartItem = (this.cart.Items || []).find(
        (ci: any) =>
          (ci.id && ci.id === payload.id) ||
          (ci.Part3DPrintName && ci.Part3DPrintName === payload.name),
      );

      if (cartItem) {
        Object.entries(payload.data).forEach((entry) => {
          const [key, val] = entry;

          if (cartItem[key] === val) {
            return;
          }

          if (key === 'Unit' && MP3DPConversionHelpers) {
            const dimension = this.printItemDimensionInUnit(cartItem.id);
            const size = MP3DPConversionHelpers.getSizeInCm(dimension.size, val);

            cartItem.Volume = MP3DPConversionHelpers.getVolumeInCm3(dimension.volume, val);
            cartItem.Surface = MP3DPConversionHelpers.getSurfaceInCm2(dimension.surface, val);
            cartItem.BoundingBoxX = size.x;
            cartItem.BoundingBoxY = size.y;
            cartItem.BoundingBoxZ = size.z;
          }

          cartItem[key] = val;
        });
      }
    },
    setCartValue(payload: any) {
      Object.entries(payload).forEach((entry) => {
        this.cart[entry[0] as any] = entry[1];
      });
    },
    setFinishValue(payload: { id?: string; name?: string; RelatedCartItemID?: string; data: any }) {
      const cartItem = (this.cart.Items || []).find(
        (ci: any) =>
          (ci.id && ci.id === payload.id) ||
          (ci.FinishName &&
            ci.RelatedCartItemID &&
            ci.FinishName === payload.name &&
            ci.RelatedCartItemID === payload.RelatedCartItemID),
      );

      if (!cartItem) {
        return;
      }

      Object.entries(payload.data).forEach((entry) => {
        cartItem[entry[0]] = entry[1];
      });
    },
    addPrintItemDocument(payload: any) {
      const cartItem = (this.cart.Items || []).find((item: any) => item.id === payload.id);

      if (!cartItem) {
        return;
      }

      if (!cartItem.GenericDocuments) {
        cartItem.GenericDocuments = [];
      }

      Object.assign(cartItem.GenericDocuments, payload.data);
    },
    addAnnotations(payload: any) {
      this.annotations.push(payload);
    },
    removeAnnotation(payload: string) {
      this.annotations.splice(
        this.annotations.findIndex((el: any) => el.id === payload),
        1,
      );
    },
    removeAnnotations(payload: string) {
      this.annotations = this.annotations.filter((el: any) => el.cartItemId !== payload);
    },
    setRessources(payload: { id: string; data: any }) {
      this.ressources[payload.id] = payload.data;
    },
    setPurchaseMode(payload: string) {
      this.purchaseMode = payload;
      sessionStorage.setItem('purchaseMode', payload);
    },
    setTestPartMode(payload: boolean) {
      this.testPartMode = payload;
      sessionStorage.setItem('testPartMode', String(this.testPartMode));
    },
    setCartMustBeCreatedInBack(payload: boolean) {
      this.cartMustBeCreatedInBack = payload;
    },

    // FIX: restored — ADD_CMS_MATERIAL_SELECTION, REMOVE_CMS_MATERIAL_SELECTION, RESET_CMS_MATERIAL_SELECTION
    // were completely missing from the Vue 3 conversion
    addCmsMaterialSelection(payload: { item: string; id: string }) {
      if (!this.CMSMaterialSelection[payload.item]) {
        this.CMSMaterialSelection[payload.item] = [];
      }

      this.CMSMaterialSelection[payload.item].push(payload.id);
    },
    removeCmsMaterialSelection(payload: { item: string }) {
      this.CMSMaterialSelection[payload.item].pop();
    },
    resetCmsMaterialSelection(payload: { item: string }) {
      this.CMSMaterialSelection[payload.item] = [];
    },

    // ─── ASYNC ACTIONS ─────────────────────────────────────────────────────────

    async createCart(payload?: any) {
      const response: any = await $fetch.post('/resources/me/carts', {
        query: { service: '3dprinting' },
        data: {},
      });

      if (payload?.noPatch) {
        return response;
      }

      this.setCart(response);

      return this.patchCart({ ...payload, forceAdd: true });
    },

    // FIX: removed dead unused import of useMeStore which was never referenced in this method
    async overwriteLocalCartWithBackendCart() {
      const response = await this.createCart({ noPatch: true });
      this.cart.id = response.id;
    },

    async createCartFromFilesInAllStorages(purge = true) {
      const me = useMeStore();
      const fileStore = useFileStore();
      const { MP3DPCartService } = await getModules();

      if (me.isLoggedIn) {
        this.overwriteLocalCartWithBackendCart();
      }

      const files = await fileStore.getFilesFromAllStorages();

      if (files.length > fileStore.files3DInRAM.length) {
        files.forEach((f: any) => {
          if (!fileStore.files3DInRAM.map((curFile: any) => curFile.name).includes(f.name)) {
            fileStore.storeFileInRAM(f);
          }
        });
      }

      const isTestPartMode = this.testPartMode;

      if (!files.length) {
        return;
      }

      const partAlreadyAdded = this.printItems.map((el: any) => el.Part3DPrintName);
      const nonSTLParts = files.filter(
        (el: any) =>
          el.name.split('.')[1] !== 'stl' && el.name !== 'Door_Hinge.CATPart' && isTestPartMode,
      );
      const partToAdd = files.filter(
        (el: any) =>
          !partAlreadyAdded.includes(el.name) ||
          (el.name === 'Door_Hinge.CATPart' &&
            isTestPartMode &&
            !partAlreadyAdded.includes(el.name)),
      );

      if (!partToAdd.length) {
        console.error('Could not add part during cart creation');
      }

      partToAdd.forEach((el: any) => {
        this.addCartItem(
          Object.assign(MP3DPCartService.getDefaultPrintItem(el.default || {}), {
            Part3DPrintName: el.name,
            Format: el.name.split('.').pop(),
            Part3DPrintDriveId: 'LOCAL',
            id: `part-${el.name}`,
          }),
        );
      });

      if (purge) {
        await Promise.all(nonSTLParts.map(async (el: any) => fileStore.removeFile(el.name)));
      }

      if (!me.isLoggedIn) {
        this.setCartMustBeCreatedInBack(true);
      }

      return this.getCartItemsSpecFromSessionStorage();
    },

    async createCartFromAnotherCart(payload: any) {
      const fromCart: any = await this.fetch(payload.id);

      await this.resetCart();

      const duplicateCart: any = await $fetch.post(
        `/resources/mdcart/carts/${fromCart.id}/duplicate`,
        {
          query: { service: '3dprinting' },
          data: {
            CartsData: [
              {
                Currency: fromCart.Currency,
                ShopID: fromCart.ShopID,
                ItemsOffersMapping: {},
                currentState: payload.status || 'DRAFT',
              },
            ],
          },
        },
      );

      this.setCart(duplicateCart[0]);

      if (payload.keep) {
        this.saveCartInLocalStorage(fromCart);
      }

      return { cart: this.cart, oldCart: fromCart };
    },

    async getCartItemsSpecFromSessionStorage(payload?: any) {
      let cachedCart: any;

      if (!payload) {
        cachedCart = localStorage.getItem('saved-cart')
          ? JSON.parse(localStorage.getItem('saved-cart')!)
          : '';

        if (!cachedCart) {
          return;
        }
      } else {
        cachedCart = payload;
      }

      cachedCart.Items.filter((el: any) => el['@type'] === 'MP3DP_PrintRequest').forEach(
        (item: any) => {
          const printItem = this.printItemByName(item.Part3DPrintName);

          if (printItem) {
            this.setCartItemValue({
              id: printItem.id,
              data: {
                BoundingBoxX: item.BoundingBoxX,
                BoundingBoxY: item.BoundingBoxY,
                BoundingBoxZ: item.BoundingBoxZ,
                Surface: item.Surface,
                Volume: item.Volume,
                Process: item.Process,
                ProcessFamily: item.ProcessFamily,
                MaterialCategory: item.MaterialCategory,
                MaterialSubCategory: item.MaterialSubCategory,
                MaterialType: item.MaterialType,
                MaterialID: item.MaterialID,
                thicknessValues: item.thicknessValues,
                thicknessUnit: item.thicknessUnit,
                Color: item.Color,
                ToleranceMin: item.ToleranceMin,
                Unit: item.Unit,
                Quantity: item.Quantity,
                DateDelivery: item.DateDelivery,
              },
            });
          }
        },
      );
    },

    async getCartSpecsFromSessionStorage() {
      if (!this['saved-cart']) {
        return;
      }

      this.setShippingAddress(this['saved-cart'].ShipTo.id);
      this.setCartValue({});
    },

    async fetch(payload: string, noSet = false) {
      // fetch is the canonical restore entry point (page refresh, deep link). The weight and
      // pricing getters (cartItemsWeight → totalWeight → shopsV10/costing params) read the
      // lazily-loaded MP3DPCartService for material densities; in the normal funnel it is
      // pulled in by earlier cart mutations, but on a cold restore nothing else loads it, so
      // totalWeight collapses to 0 and every price computation early-returns. Ensure it is
      // ready before the restored state feeds the price pipeline.
      await getModules();

      const response: any = await $fetch.get(`/resources/mdcart/carts/${payload}`, {
        query: {
          service: '3dprinting',
          expand: 'cart-items',
        },
      });

      if (!noSet) {
        this.setCart(response);
      }

      return response;
    },

    async patchCart(payload?: any) {
      const { MP3DPCartService } = await getModules();
      const me = useMeStore();

      if (!me.isLoggedIn) {
        this.saveCartInLocalStorage();
        return;
      }

      let patchArray: any[] = [].concat((payload || {}).data || []);
      let patchBonus: any;

      payload = payload || {};

      const isEditable = (cartItem: any) =>
        ((cartItem.id && cartItem.backendId) ||
          (cartItem.id && cartItem.type && !cartItem.type !== 'MP3DP_PrintRequest')) &&
        !payload.forceAdd &&
        !cartItem.forceAdd;
      let additionalPatchForFinishes: any[] = [];

      const addItemToPatchArray = (cartItem: any) => {
        const editable = isEditable(cartItem);
        this.convertRelatedCartItemIdToBackendId(cartItem);
        const isFinishAddedBeforeFirstPatch =
          cartItem.RelatedCartItemID?.startsWith('part-') &&
          (cartItem.type === 'MP3DP_FinishRequest' || cartItem['@type'] === 'MP3DP_FinishRequest');

        if (isFinishAddedBeforeFirstPatch) {
          additionalPatchForFinishes.push(cartItem);
        } else {
          patchArray.push({
            op: editable ? 'modify' : 'add',
            path: `/cart-items/${editable ? cartItem.backendId || cartItem.id : ''}`,
            value: MP3DPCartService.getPatchableCartItems(cartItem, editable ? 'modify' : 'add'),
          });
        }
      };

      if (!payload.patchCartOnly) {
        if (payload.addMissingFinishes?.length) {
          payload.addMissingFinishes.forEach((cartItem: any) => {
            addItemToPatchArray(cartItem);
          });
          additionalPatchForFinishes = [];
        }

        this.cartItems.forEach((cartItem: any) => {
          addItemToPatchArray(cartItem);
        });
      }

      if (payload.patchCart || payload.patchCartOnly) {
        const [Utils] = await requirejs<any[]>(['UWA/Utils']);
        patchBonus = { multibiddingId: Utils.getUUID() };

        if (payload.isTender) {
          patchBonus.TenderData = this.shopsV10Params;
          delete patchBonus.TenderData.deliveryCountryIso3;
          delete patchBonus.TenderData.sort;

          if (payload.area) {
            patchBonus.TenderData.area = payload.area;
          }
        }

        patchArray.push({
          op: 'modify',
          path: '/',
          value: Object.assign(
            patchBonus,
            MP3DPCartService.getPatchableCartItems(this.cart, 'modify'),
          ),
        });
      }

      if (payload?.only) {
        patchArray = patchArray.filter((el: any) => el.op === payload.only);
      }

      return (
        $fetch.patch(`/resources/mdcart/carts/${this.cart.id}`, {
          query: { service: '3dprinting' },
          data: { patchArray },
        }) as Promise<any>
      ).then(async (response: any) => {
        if (payload.only !== 'remove') {
          const oldPrintItemsDFMStatus = this.printItems.reduce((acc: any, item: any) => {
            if (!acc[item.id] && item.id && item.DFMReportStatus) {
              acc[item.id] = item.DFMReportStatus;
            }

            return acc;
          }, {});
          const allPrintItemsId = this.printItems.map((el: any) => el.id);
          const newPrintItemsId = response.Items.filter(
            (el: any) => el.type === 'MP3DP_PrintRequest',
          ).map((el: any) => el.id);
          const newPrintItemId = newPrintItemsId.find(
            (id: string) => !allPrintItemsId.includes(id),
          );

          if (newPrintItemId) {
            this.setCurrentPrintItem(newPrintItemId);
          }

          if (!payload.silent) {
            this.setCart(response);
          }

          Object.entries(oldPrintItemsDFMStatus).forEach((entry) => {
            this.setCartItemValue({ id: entry[0], data: { DFMReportStatus: entry[1] } });
          });

          localStorage.setItem('vue-cart-id', this.cart.id);
          this.saveCartInLocalStorage();
        }

        if (
          additionalPatchForFinishes &&
          additionalPatchForFinishes.length &&
          !payload.addMissingFinishes
        ) {
          await this.patchCart({ addMissingFinishes: additionalPatchForFinishes });
        }

        if (payload?.noPull) {
          return response;
        }

        if (patchArray.map((a: any) => a.value).filter((e: any) => e.resourceURL).length) {
          return this._pulling(this.cart.id).then((res: any) => {
            res.Items.filter((item: any) => item.type === 'MP3DP_PrintRequest').forEach(
              (item: any) => {
                this.setCartItemValue({
                  id: `part-${item.Part3DPrintName}`,
                  data: { Part3DPrint: item.Part3DPrint },
                });
              },
            );
          });
        }
      });
    },

    async _pulling(cartId: string) {
      const { MP3DPTracker } = await getModules();
      let nbrTry = 0;
      let trackingHasBeenDone = false;

      return new Promise((resolve) => {
        (async function pull(this: any) {
          return $fetch
            .get(`/resources/mdcart/carts/${cartId}`, {
              query: {
                service: '3dprinting',
                expand: 'cart-items',
              },
            })
            .then((response: any) => {
              if (
                !response.Items.filter((el: any) => el.type === 'MP3DP_PrintRequest').every(
                  (el: any) => el.Part3DPrintStatus === 'UPLOADED' && el.Part3DPrint,
                )
              ) {
                nbrTry += 1;

                if (nbrTry > 5 && !trackingHasBeenDone) {
                  MP3DPTracker.event('ERROR:CartPulling', 'ERROR', 'Passive');
                  trackingHasBeenDone = true;
                }

                pullingTimeoutIds.push(setTimeout(pull.bind(this), 2000));
              } else {
                if (nbrTry > 5) {
                  MP3DPTracker.event('WARNING:CartPullingLong', 'WARNING', 'Passive', [
                    nbrTry * 2000,
                  ]);
                }

                resolve(response);
              }
            });
        }).call(this);
      });
    },

    async deleteCart(id: string) {
      return $fetch.remove(`/resources/mdcart/carts/${id}`, {
        query: { service: '3dprinting' },
      });
    },

    async sendCart(payload: any) {
      const me = useMeStore();
      const labExploration = useLabExplorationStore();

      const labsToSend = labExploration.getLabs(this.selectedLabs.map((el: any) => el.id));
      const idOfTheLabWeSendFirst = payload.isTender ? undefined : labsToSend[0].id;
      const initialPatchArray: any[] = [];
      let prm = Promise.resolve() as any;

      payload = payload || {};

      if (payload.isDirectPurchase) {
        await this.cleanupIncompatibleFinishesAndServices({ isDirectPurchase: true });
      }

      this.setCartValue({
        Currency: this.lab(idOfTheLabWeSendFirst).supportedCurrencies,
        currentState: 'SUBMITTED',
        IsDirectPurchase: payload.isDirectPurchase ? 'TRUE' : 'FALSE',
        IsPriceDetailsDisplay: true,
        ShipTo: this.shippingAddressId,
        ShopID: idOfTheLabWeSendFirst,
      });

      if (!payload.isTender) {
        this.setCartItemValue({
          id: this.deliveryItem.id,
          data: {
            currentState: 'SUBMITTED',
            OfferID: this.labShippingOffer[idOfTheLabWeSendFirst],
            ShopID: idOfTheLabWeSendFirst,
            CartID: this.cart.id,
          },
        });

        this.printItems.forEach((item: any) => {
          this.setCartItemValue({
            id: item.id,
            data: {
              ItemWeight: this.cartItemsWeight[item.id],
              currentState: 'SUBMITTED',
              ShopID: idOfTheLabWeSendFirst,
              OfferID: this.labSelectedOffers(idOfTheLabWeSendFirst)[item.id].id,
            },
          });
        });

        this.finishItems.forEach((finish: any) => {
          let offerId = '';
          let matchingOffer: any;

          if (this.labFinishOffers(idOfTheLabWeSendFirst).length) {
            matchingOffer = this.labFinishOffers(idOfTheLabWeSendFirst).find(
              (offer: any) => offer.product.optionName === finish.FinishName,
            );

            if (matchingOffer) {
              offerId = matchingOffer.id;
            }
          }

          this.setCartItemValue({
            id: finish.id,
            data: {
              OfferID: offerId,
              CartID: this.cart.id,
              currentState: 'SUBMITTED',
              ShopID: idOfTheLabWeSendFirst,
            },
          });
        });

        this.servicesItems.forEach((additionalService: any) => {
          let offerId = '';
          let matchingOffer: any;

          if (this.labServiceOffers(idOfTheLabWeSendFirst).length) {
            matchingOffer = this.labServiceOffers(idOfTheLabWeSendFirst).find(
              (offer: any) =>
                offer.product.additionalServiceName === additionalService.AdditionalServiceName,
            );

            if (matchingOffer) {
              offerId = matchingOffer.id;
            }
          }

          this.setCartItemValue({
            id: additionalService.id,
            data: {
              OfferID: offerId,
              CartID: this.cart.id,
              currentState: 'SUBMITTED',
              ShopID: idOfTheLabWeSendFirst,
            },
          });
        });
      } else {
        this.setCartItemValue({ id: this.deliveryItem.id, data: { currentState: 'SUBMITTED' } });
        this.printItems.forEach((item: any) => {
          this.setCartItemValue({ id: item.id, data: { currentState: 'SUBMITTED' } });
        });
        this.finishItems.forEach((finish: any) => {
          this.setCartItemValue({ id: finish.id, data: { currentState: 'SUBMITTED' } });
        });
        this.servicesItems.forEach((svc: any) => {
          this.setCartItemValue({ id: svc.id, data: { currentState: 'SUBMITTED' } });
        });
      }

      Object.values(this.documents).forEach((documentID: any) => {
        initialPatchArray.push({
          op: 'add',
          path: '/bidding-documents',
          value: {
            CartItemID: this.currentPrintItemGetter.backendId || this.currentPrintItemGetter.id,
            DocumentID: documentID,
          },
        });
      });

      Object.values(this.annotations).forEach((el: any) => {
        initialPatchArray.push({
          op: 'add',
          path: '/bidding-documents',
          value: { CartItemID: el.cartItemId, DocumentID: el.id },
        });
      });

      if (this.contract.id) {
        prm = me
          .linkContractDocumentToSeller({
            documentID: this.contract.id,
            consumerID: this.selectedLabs.length
              ? this.lab(idOfTheLabWeSendFirst).owner.split('/').pop()
              : '',
          })
          .then((contract: any) => {
            initialPatchArray.push({
              op: 'add',
              path: '/contracts',
              value: {
                CartItemID: this.currentPrintItemGetter.backendId || this.currentPrintItemGetter.id,
                ContractID: contract.id,
              },
            });
          });
      }

      initialPatchArray.push({
        op: 'add',
        path: '/message',
        value: {
          author: `/mdpeoplecompany/people/${me.me?.id}`,
          documentIDs: Object.values(this.documents).concat(
            this.annotations.map((el: any) => el.id),
          ),
          message:
            payload.message +
            (this.contract.id ? ' \n \n A NDA document has been attached to this order' : ''),
        },
      });

      const { MP3DPCartService } = await getModules();

      this.services.forEach((service: any) => {
        initialPatchArray.push({
          op: 'add',
          path: '/cart-items/',
          value: MP3DPCartService.getDefaultServicePrintItem({
            AdditionalServiceName: service,
            Quantity: this.printItems.length,
            OfferID: this.labServiceOffers(idOfTheLabWeSendFirst).length
              ? this.labServiceOffers(idOfTheLabWeSendFirst).find(
                  (el: any) => el.product.additionalServiceName === service,
                ).id
              : '',
            CartID: this.cart.id,
            currentState: 'SUBMITTED',
            ShopID: idOfTheLabWeSendFirst,
          }),
        });
      });

      prm = prm
        .then(async () =>
          this.patchCart({
            isTender: payload.isTender,
            area: payload.area,
            patchCart: true,
            data: initialPatchArray,
            noPull: true,
          }),
        )
        .catch(() => {
          throw new Error('Path failed');
        });

      if (labsToSend.length > 1) {
        const multibiddingLabs = labsToSend.filter((lab: any) => lab.id !== idOfTheLabWeSendFirst);

        return prm.then(async () => {
          const params = {
            CartsData: multibiddingLabs.map((selectedLab: any) => {
              const lab = this.lab(selectedLab.id);
              const pricingParamOffer = (this.pricingParams[lab.id] || []).flatMap(
                (el: any) => el.offers,
              );
              const offersMap = Object.fromEntries(
                this.cartItems
                  .map((item: any) => {
                    let offerId: string | undefined;

                    if (
                      item.type === 'MP3DP_PrintRequest' &&
                      item.Process !== '' &&
                      item.MaterialID !== ''
                    ) {
                      offerId = (
                        pricingParamOffer.find((el: any) => el.cartItemId === item.id) || { id: '' }
                      ).id;
                    } else if (item.type === 'MP_DeliveryServiceRequest') {
                      if (item.DeliveryServiceName === 'Shipping') {
                        offerId = this.labShippingOffer[lab.id];
                      } else {
                        offerId = this.labPickupOffer[lab.id];
                      }
                    } else if (
                      item.type !== 'MP3DP_PrintRequest' &&
                      item.type !== 'MP_DeliveryServiceRequest'
                    ) {
                      offerId = (
                        pricingParamOffer.find((el: any) => el.cartItemId === item.id) || { id: '' }
                      ).id;
                    }

                    return offerId ? [item.id, offerId] : [];
                  })
                  .filter((map: any[]) => map.length),
              );

              return {
                Currency: lab.supportedCurrencies,
                ItemsOffersMapping: offersMap,
                ShopID: lab.id,
              };
            }),
          };

          return $fetch.post(`/resources/mdcart/carts/${this.cart.id}/duplicate`, {
            query: { service: '3dprinting' },
            data: params,
          });
        });
      }

      return prm;
    },

    saveCartInLocalStorage(payload?: any) {
      const cartToSave = payload || this.cart;
      localStorage.removeItem('saved-cart');
      localStorage.setItem('saved-cart', JSON.stringify(cartToSave));
      this.setSavedCart(cartToSave);
    },

    async resetCart(payload?: any) {
      const fileStore = useFileStore();

      this.resetStore(payload);

      fileStore.clearFilesInRAM();

      if (!payload || (payload && !payload.noResetFiles)) {
        await fileStore.removeFiles();
      }

      if (!payload || (payload && !payload.noResetThumbnails)) {
        await fileStore.removeThumbnails();
      }

      if (!payload || (payload && !payload.noResetMode)) {
        this.setPurchaseMode('');
      }

      if (!payload || (payload && !payload.noResetEstimatedCountry)) {
        this.setEstimationCountryShippingPrice('');
      }

      if (!payload || (payload && !payload.noResetExploreMode)) {
        this.setExploreMode(false);
      }

      if (!payload || (payload && !payload.noResetLabs)) {
        this.setSelectedLabs([]);
      }

      if (!payload || (payload && !payload.noResetVueCartId)) {
        localStorage.removeItem('vue-cart-id');
      }

      if (!payload || (payload && !payload.noResetTestPartMode)) {
        this.setTestPartMode(false);
      }

      if (!payload || (payload && !payload.noResetSavedCart)) {
        localStorage.removeItem('saved-cart');
      }
    },

    async computeShippingPrice() {
      const me = useMeStore();

      if (this.totalWeight === 0 || this.cart.currentState === 'SUBMITTED' || !me.isLoggedIn) {
        // loadCompute() flips isShippingLoading on before calling us (so the shimmer shows
        // during the throttle window). Bailing out without clearing it — which happens on a
        // logged-in refresh when this runs before fetchMe resolves — leaves the price
        // summary stuck on its skeleton forever.
        this.setLoading('isShippingLoading', false);
        return;
      }

      this.setLoading('isShippingLoading', true);

      try {
        const shippingPrices = await Promise.all(
          this.selectedLabs.map(async (lab: any) => {
            this.setShippingPrice({ id: lab.id, price: null });

            const response: any = await $fetch.post(
              `/resources/mdshop/shops/${lab.id}/shipping-computation`,
              {
                cache: true,
                data: {
                  items: this.printItems.map((el: any) => ({
                    weight: this.cartItemsWeight[el.id],
                    quantity: Number.parseInt(el.Quantity, 10),
                  })),
                  country: this.shippingAddressCode || this.estimationShippingCountry,
                },
              },
            );

            const price =
              response.price === '-' || parseInt(response.price, 10) === -42 ? '' : response.price;

            this.setShippingPrice({ id: lab.id, price });

            return { id: lab.id, price };
          }),
        );

        return shippingPrices;
      } finally {
        // Must run even if a shipping-computation request rejects, otherwise the flag stays
        // true and the price summary keeps its shimmer skeleton forever.
        this.setLoading('isShippingLoading', false);
      }
    },

    async addPrintItems(payload: any[]) {
      const { MP3DPCartService, MP3DPOrderHelpers } = await getModules();
      const me = useMeStore();

      if (!me.isLoggedIn) {
        return;
      }

      const partAlreadyAdded = this.printItems.map((el: any) =>
        el.Part3DPrintName.replace('_V2', ''),
      );
      // Names already taken in the cart. The parts of this payload are only pushed into the
      // cart further down (in the Promise.all), so this.printItems does not see them yet:
      // without tracking them here, two parts of the same batch sharing a name would both
      // keep it and end up as two cart items with the same Part3DPrintName and the same
      // `part-<name>` id.
      const takenNames = this.printItems.map((el: any) => el.Part3DPrintName);
      const reserveName = (name: string) => {
        partAlreadyAdded.push(name);
        takenNames.push(name);
      };
      const partToAdd = payload.reduce((acc: any[], toAdd: any) => {
        if (!partAlreadyAdded.includes(toAdd.fileTitle)) {
          reserveName(toAdd.fileTitle);
          acc.push(toAdd);

          return acc;
        }

        toAdd.fileTitle = MP3DPOrderHelpers.getNewPartName(toAdd.fileTitle);

        while (takenNames.includes(toAdd.fileTitle)) {
          toAdd.fileTitle = MP3DPOrderHelpers.getNewPartName(toAdd.fileTitle);
        }

        reserveName(toAdd.fileTitle);
        acc.push(toAdd);

        return acc;
      }, []);

      await Promise.all(
        partToAdd.map(async (part: any) => {
          const isDrivePart = Boolean(part.resourceURL) || part.drive;
          let drivePartUrl = null;

          if (isDrivePart) {
            // The part is stored on its own tenant's 3DDrive, which has its own host
            const driveUrl = await $fetch.getServiceURL('3DDrive', part.tenant);
            drivePartUrl = `${driveUrl}/resources/3ddrive/v1/bos/${part.backendId}/fileurl?tenant=${part.tenant}`;
            this.setRessources({
              id: part.fileTitle,
              data: { url: drivePartUrl.split('enovia')[1], tenant: part.tenant },
            });
          }

          const partName = part.fileTitle || part.Part3DPrintName;

          this.addCartItem(
            Object.assign(MP3DPCartService.getDefaultPrintItem(part.default || {}), {
              id: `part-${partName}`,
              backendId: part.backendId,
              Part3DPrintName: partName,
              Part3DPrint: isDrivePart ? '' : part.backendId || part.Part3DPrint,
              Part3DPrintDriveId: part.Part3DPrintDriveId || part.backendId || part.Part3DPrint,
              Format: part.format,
              resourceURL: isDrivePart ? drivePartUrl : null,
            }),
          );
        }),
      );

      return partToAdd;
    },

    async removeItems(ids: string[]) {
      const printItems = this.cartItems.filter((el: any) => ids.includes(el.id));

      if (!printItems.length) {
        return;
      }

      await this.patchCart({
        data: printItems.map((item: any) => ({
          op: 'remove',
          path: `/cart-items/${item.backendId || item.id}`,
        })),
        only: 'remove',
        noPull: true,
      });

      ids.forEach((id: string) => {
        this.removeItem(id);
      });
    },

    setPrintItem(payload: string) {
      this.setCurrentPrintItem(payload);
    },

    async updateFinishes(payload: { id: string; finishes: string[] }) {
      const printItemFinishes = this.finishItemsByCartItem[payload.id] || [];
      let finishes: any[] = [];

      const { MP3DPCartService } = await getModules();

      if (!payload.finishes.length || printItemFinishes.length > payload.finishes.length) {
        finishes = printItemFinishes.filter((el: any) => !payload.finishes.includes(el.FinishName));

        await this.removeItems(finishes.filter((el: any) => el.id).map((el: any) => el.id));
        finishes
          .filter((el: any) => !el.id)
          .forEach((finish: any) => {
            this.removeItem(finish);
          });
      } else {
        finishes = payload.finishes.filter(
          (el: any) => !printItemFinishes.map((finish: any) => finish.FinishName).includes(el),
        );

        finishes.forEach((finish: any) => {
          this.addCartItem(
            MP3DPCartService.getDefaultFinishPrintItem({
              FinishName: finish,
              RelatedCartItemID: payload.id,
              CartID: this.cart.id,
              currentState: 'DRAFT',
              Quantity: this.printItem(payload.id).Quantity || 1,
            }),
          );
        });
      }

      return finishes;
    },

    async removeFinishesOfPrintItem(payload: string) {
      return this.updateFinishes({ id: payload, finishes: [] });
    },

    async uploadContractDocument(payload: File) {
      const formData = new FormData();
      formData.append('documentFile', payload, payload.name);
      this.setNdaDocument({ id: '', name: payload.name });

      try {
        const result: any = await $fetch.post('/resources/mdcontract/contract-documents', {
          query: { service: '3dprinting', 'document-type': 'MP_NDADocument' },
          form: formData,
        });
        this.setNdaDocument({ id: result.documentID, name: payload.name });
      } catch (error) {
        this.setNdaDocument({});
        throw new Error(error as string);
      }
    },

    async uploadBiddingDocuments(files: File[]) {
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append('file', file, file.name);
        this.addDocument({ id: '', name: file.name });
      });

      try {
        const result: any = await $fetch.post('/resources/mdcart/bidding-documents', {
          query: { service: '3dprinting', 'document-type': 'MP_NDADocument' },
          form: formData,
          timeout: 300000,
        });
        Object.keys(result.documentIDs).forEach((id: string) => {
          this.addDocument({ id, name: result.documentIDs[id] });
        });
      } catch (error) {
        files.forEach((file: File) => {
          this.removeDocument({ name: file.name });
        });
        throw new Error(error as string);
      }
    },

    async uploadThumbnailToBackend(payload: any) {
      const formData = new FormData();
      formData.append('thumbnail', payload.image, 'thumbnail.png');

      let cartItemId = null;

      if (payload.partId.startsWith('part-')) {
        cartItemId = this.printItems.find(
          (it: any) => it.Part3DPrintName === payload.name,
        )?.backendId;
      } else {
        cartItemId = payload.backendId;
      }

      await $fetch.post(`/resources/mdcart/cart-items/${cartItemId}/thumbnail`, {
        query: { service: '3dprinting' },
        form: formData,
      });
    },

    async uploadThumbnailsToBackend(payload: any[]) {
      await Promise.all(
        payload.map(async (thumbnail: any) => {
          await this.uploadThumbnailToBackend(thumbnail);
        }),
      );
    },

    // FIX: restored correct Promise.all wrapping for the members fetch to preserve the original
    // return shape (array of arrays), which the subsequent .flat() call depends on.
    async checkLabsMembers(payload: any = {}) {
      const me = useMeStore();

      await this.waitLabsAreLoaded();
      const labs = payload.ids ? payload.ids.map((id: string) => ({ id })) : this.selectedLabs;

      const members = await Promise.all(
        labs.map(async (lab: any) => {
          let { owner, IsInternal } = this.lab(lab.id);

          if (!owner || !IsInternal) {
            const result: any = await $fetch.get(`/resources/mdshop/public/shops/${lab.id}`, {
              cache: true,
              query: { service: '3dprinting' },
            });
            owner = result.owner;
            IsInternal = result.IsInternal;
          }

          const labCompanyId = owner.split('/').pop();

          if (labCompanyId !== me.companyId) {
            return Promise.resolve([]);
          }

          // FIX: wrap in Promise.all to preserve original array-of-arrays shape for the .flat() below
          const promises: Array<Promise<any>> = [];
          promises.push(
            $fetch.get(`/resources/mdshop/shops/${lab.id}/members`, {
              query: { service: '3dpriting' },
              cache: true,
            }) as Promise<any>,
          );

          return Promise.all(promises).then((el) => el.flat());
        }),
      );

      const memberRoles = members
        .flat()
        .filter((el: any) => el.id === me.me?.id)
        .map((el: any) => el.Roles)
        .flat();

      if (!memberRoles.length || (memberRoles.length === 1 && memberRoles[0] === 'Buyer')) {
        return;
      }

      throw new Error("You can't submit a request to a lab you are a member of");
    },

    async getFile(payload: any) {
      if (!payload.Part3DPrint) {
        console.error('getFile : Cannot find Part3DPrint');
      }

      const file: any = await $fetch.get(`/resources/mdcart/documents/${payload.Part3DPrint}`, {
        cache: false,
      });

      return {
        format: file.docName.split('.').pop(),
        url: file.docUrl,
        name: file.docName,
      };
    },

    async getFileFromRessourceUrl(payload: any) {
      const item = this.ressources[payload.name];
      const file: any = await $fetch.get(item.url, {
        service: '3DDrive',
        tenant: item.tenant,
        cache: false,
      });

      return { format: file.extension, url: file.url };
    },

    async cleanupIncompatibleFinishesAndServices({
      isDirectPurchase,
    }: { isDirectPurchase?: boolean } = {}) {
      const finishes = this.finishItems;
      const services = this.servicesItems;
      const itemsToRemove: any[] = [];

      if (!finishes.length && !services.length) {
        return;
      }

      this.selectedLabs.forEach((lab: any) => {
        const finishesName = this.labFinishOffers(lab.id).map((el: any) => el.product.optionName);
        const servicesName = this.labServiceOffers(lab.id).map(
          (el: any) => el.product.additionalServiceName,
        );

        finishes.forEach((finish: any) => {
          if (!finishesName.includes(finish.FinishName)) {
            itemsToRemove.push(finish);
          }

          if (
            isDirectPurchase &&
            finishesName.includes(finish.FinishName) &&
            !this.labFinishOffers(lab.id).find(
              (el: any) =>
                el.product.optionName === finish.FinishName && el.pricingOption === 'ExactPrice',
            )
          ) {
            itemsToRemove.push(finish);
          }
        });

        services.forEach((service: any) => {
          if (!servicesName.includes(service.FinishName)) {
            itemsToRemove.push(service);
          }
        });
      });

      return this.removeItems(
        itemsToRemove
          .filter((el: any) => el.id)
          .map((el: any) => el.id)
          .filter((el: any, index: number, arr: any[]) => arr.indexOf(el) === index),
      );
    },

    checkPartsDimensions() {
      return false;
    },

    async saveAnnotationDocument(payload: any) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
      const fileName = `3dannotation_${payload.parentDocumentID}.json`;
      const file = new File([blob], fileName);
      const formData = new FormData();
      formData.append('file', file, fileName);

      const response: any = await $fetch.post('/resources/mdcart/bidding-documents', {
        form: formData,
      });
      const id = Object.keys(response.documentIDs)[0];

      if (id) {
        this.addAnnotations({
          id,
          cartItemId: payload.CartItemId,
          annotation: payload.JSONAnnotations,
        });
      }
    },

    async discardOptimization(payload: any) {
      const idToSet = payload.item.RepairedDocumentID || payload.item.OriginalSTLDocumentID;
      const oldFile = await this.getFile({ Part3DPrint: idToSet });

      this.setCartItemValue({
        id: payload.item.id,
        data: {
          Part3DPrintName: oldFile.name,
          Part3DPrint: idToSet,
          Volume: 0,
          LatticeDocumentID: payload.reset ? '' : payload.item.LatticeDocumentID,
          Format: oldFile.format,
        },
      });
    },

    fillPrintItemsWithBestOffers(payload: any) {
      const labOffers = this.labSelectedOffers(payload.lab.id);

      if (Object.values(labOffers).length && payload.lab.shopServices.MP3DP_PrintShopService) {
        payload.printItems.forEach((item: any) => {
          let bestOffer: any;
          const product = payload.lab.shopServices.MP3DP_PrintShopService.products
            .filter((el: any) => el.type === 'MP3DP_Product')
            .find((p: any) => {
              bestOffer = p.offers.find(
                (offer: any) => labOffers[item.id] && offer.id === labOffers[item.id].id,
              );

              if (!bestOffer) {
                bestOffer = p.offers.find(
                  (offer: any) =>
                    labOffers[`part-${item.Part3DPrintName}`] &&
                    offer.id === labOffers[`part-${item.Part3DPrintName}`].id,
                );
              }

              return bestOffer;
            });

          this.setCartItemValue({
            id: item.id,
            data: {
              OfferId: bestOffer.id,
              Process: product.processId,
              ProcessFamily: product.processFamilyId,
              MaterialCategory: bestOffer.materialFamilyId,
              MaterialSubCategory: bestOffer.materialClassId,
              MaterialType: bestOffer.materialTypeId,
              MaterialID: bestOffer.materialId,
            },
          });
        });
      }
    },

    async applyOptimizationFromStoredLatticeId(payload: any) {
      if (!payload.item.LatticeDocumentID) {
        return;
      }

      const oldFile = await this.getFile({ Part3DPrint: payload.item.LatticeDocumentID });

      this.setCartItemValue({
        id: payload.item.id,
        data: {
          Part3DPrintName: oldFile.name,
          Part3DPrint: payload.item.LatticeDocumentID,
          Volume: 0,
          Format: oldFile.format,
        },
      });
    },

    async sendUserFeedback(payload: any) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const formData = new FormData();
      formData.append('message', blob);

      await $fetch.post('/resources/mdpeoplecompany/feedback', {
        query: { service: '3dprinting' },
        form: formData,
      });
    },

    async waitPrintItemDimensionsIsLoaded(payload: { id: string }): Promise<void> {
      if (this.dimensionsHasBeenCalculatedByPrintItemId[payload.id]) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const unwatch = this.$subscribe((_mutation: any, state: any) => {
          if (
            state.cart?.Items?.some(
              (item: any) =>
                item.id === payload.id &&
                (parseFloat(item.BoundingBoxX) ||
                  parseFloat(item.BoundingBoxY) ||
                  parseFloat(item.BoundingBoxZ)),
            )
          ) {
            unwatch();
            resolve();
          }
        });
      });
    },

    async waitLabsAreLoaded(): Promise<void> {
      if (!this.isLabsLoading) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        if (!this.selectedLabs.length) {
          resolve();
          return;
        }

        const unwatch = this.$subscribe((_mutation: any, state: any) => {
          if (!state.isLabsLoading) {
            unwatch();
            resolve();
          }
        });
      });
    },

    stopPulling() {
      pullingTimeoutIds.forEach((t) => {
        clearTimeout(t);
      });
      pullingTimeoutIds = [];
    },
  },
});
